/**
 * TypeScript types matching the Mappings Response schema v1.0.
 *
 * Canonical schema: `packages/core/src/types/mappings.schema.json`.
 * Endpoint: `GET <baseUrl>/destinations/mappings.json?slugs=<comma-separated>`.
 *
 * The endpoint returns a `MappingsResponse` envelope `{ destinations, generatedAt }`,
 * where each `Destination` is a discriminated union by `type` carrying the
 * per-destination mapping bundle (slug, version, config, mappings[]).
 */

/** Top-level destination adapter type. */
export type DestinationType = 'insider_web_sdk' | 'gtm_data_layer'

/** Source domain the mapping should match against `event.domain`. */
export type SourceDomain =
  | 'unknown'
  | 'browser'
  | 'email'
  | 'querobolsa_app'
  | 'megaturbo_app'
  | 'stripe'

/** Filter rule operators. */
export type FilterOp =
  | 'eq'
  | 'neq'
  | 'exists'
  | 'missing'
  | 'contains'
  | 'starts_with'

/** Insider-specific target type — which Insider collection receives the payload. */
export type InsiderTargetType =
  | 'product'
  | 'cart'
  | 'purchase'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'user'
  | 'custom_event'
  | 'home'
  | 'category'
  | 'search'
  | 'other'

// ───────────────────────────────────────────────────────────────────────────
// Mapping primitives
// ───────────────────────────────────────────────────────────────────────────

/**
 * A single field transformation: path on the canonical event view → path on
 * the destination payload. `default` is the literal fallback when `from`
 * resolves to null/undefined/''.
 *
 * **Array iteration:** `from` may contain a single `[]` segment to iterate
 * over an array (e.g. `attributes.items[].product_id`). When `to` also has
 * `[]`, fields sharing the same array prefix in `to` are correlated by
 * index — they all build the same `items[i]` sub-object.
 *
 * **Coerce:** `coerce` casts the resolved value with `Number()` / `String()`
 * / `Boolean()`; for `'boolean'`, the strings `'true'` / `'false'` map to
 * `true` / `false`. Applied AFTER `default` resolution. `'number'` returns
 * `undefined` for NaN/Infinity (the field is dropped).
 *
 * **Origin:** `coerce` can come from the backend (rum-api derives it from the
 * destination schema's leaf type) or from the bundled JSONs.
 */
export interface FieldMapping {
  /** Canonical-view path (e.g. `attributes.product_id`, `attributes.items[].id`, `metadata.user.id`). Optional when only `default` is used. */
  from?: string
  /** Destination path (dot notation; `arr[].key` produces arrays). */
  to: string
  /** Literal fallback value. */
  default?: string | number | boolean
  /** Optional type cast applied after `default` resolution. Served by the backend (derived from the destination schema) or set in the bundled JSONs. */
  coerce?: 'number' | 'string' | 'boolean'
}

/** Filter rule — combined inside a mapping per `Mapping.ruleLogic` ('and' by default). */
export interface FilterRule {
  /** Canonical-view path. */
  path: string
  op: FilterOp
  /** Required for `eq`/`neq`/`contains`/`starts_with`; forbidden for `exists`/`missing`. */
  value?: string
}

/**
 * How a mapping's `rules` combine. Absent = 'and' (every rule must pass — the
 * legacy/default behavior). 'or' = any rule passing matches. Mirrors the
 * server engine + the rum-api contract (mappings schema v1.1, additive).
 */
export type RuleLogic = 'and' | 'or'

// ── Per-mapping config (discriminated by parent destination `type`) ────────

export interface InsiderMappingConfig {
  targetType: InsiderTargetType
  [key: string]: unknown
}

/**
 * GTM per-mapping config. The backend contract is an EMPTY object — GTM has a
 * single flat payload shape, so nothing discriminates per mapping (the GA4
 * event name is carried by a `fields[]` entry with `to: 'event'`). Kept open
 * so future additive backend keys don't fail validation.
 *
 * Mirrors `gtm_data_layer.mappingConfigSchema` on the admin-api plugin.
 */
export type GtmMappingConfig = Record<string, unknown>

export type MappingConfig = InsiderMappingConfig | GtmMappingConfig

// ── Per-mapping shape — base fields shared, config discriminated ───────────

interface BaseMapping {
  id: number
  eventName: string
  source: SourceDomain
  fields: FieldMapping[]
  rules: FilterRule[]
  /** How `rules` combine. Optional; absent = 'and' (backward-compatible). */
  ruleLogic?: RuleLogic
}

export interface InsiderMapping extends BaseMapping {
  config: InsiderMappingConfig
}

export interface GtmMapping extends BaseMapping {
  config: GtmMappingConfig
}

/**
 * Generic `Mapping` retained as a structural alias for tests and code that
 * doesn't need to discriminate on destination type. New code should prefer
 * `InsiderMapping`/`GtmMapping` so `config` narrows automatically.
 */
export interface Mapping extends BaseMapping {
  config: MappingConfig
}

// ── Destination configs (discriminated by parent destination `type`) ──────

export interface InsiderDestinationConfig {
  partner: string
  [key: string]: unknown
}

/**
 * GTM destination-wide config. Usually `{}` on the wire — the admin only
 * persists `dataLayerVar` when it differs from the `'dataLayer'` default.
 */
export interface GtmDestinationConfig {
  /** Global array the SDK pushes into (`window[dataLayerVar]`). Default: `'dataLayer'`. */
  dataLayerVar?: string
  [key: string]: unknown
}

export type DestinationConfig = InsiderDestinationConfig | GtmDestinationConfig

// ───────────────────────────────────────────────────────────────────────────
// Destination — single bundle in the response, discriminated by `type`
// ───────────────────────────────────────────────────────────────────────────

interface BaseDestination<T extends DestinationType> {
  /**
   * Base slug, without the `-v<digits>` suffix (e.g. `insider-default`).
   * Mirrors the `destinations.slug` column on the rum-api. The SDK combines
   * `${slug}-${version}` to form the `MappingsRegistry` key and the value it
   * sends in `?slugs=`.
   */
  slug: string
  /**
   * Adapter payload-shape version in `v<digits>` format (e.g. `v1` for
   * `InsiderAdapterV1`). Mirrors the `destinations.version` column on the
   * rum-api.
   */
  version: string
  type: T
  /** ISO timestamp — optional carry-over for debug; envelope holds the canonical one. */
  generatedAt?: string
}

export interface InsiderDestination extends BaseDestination<'insider_web_sdk'> {
  config: InsiderDestinationConfig
  mappings: InsiderMapping[]
}

export interface GtmDestination extends BaseDestination<'gtm_data_layer'> {
  config: GtmDestinationConfig
  mappings: GtmMapping[]
}

/** A single destination bundle in the response — discriminated by `type`. */
export type Destination = InsiderDestination | GtmDestination

/**
 * Top-level envelope returned by `GET /destinations/mappings.json`.
 * Mirrors backend `PublicMappingsResponse` from `@montilla/types`.
 */
export interface MappingsResponse {
  destinations: Destination[]
  /** ISO 8601 timestamp of the snapshot on the server. */
  generatedAt: string
}

// ───────────────────────────────────────────────────────────────────────────
// Validation error
// ───────────────────────────────────────────────────────────────────────────

/**
 * Thrown by `validateDestination(json)` when the payload doesn't conform to
 * the schema. Carries the raw ajv errors so callers can log details.
 */
export class MappingsValidationError extends Error {
  readonly errors: unknown
  constructor(message: string, errors: unknown) {
    super(message)
    this.name = 'MappingsValidationError'
    this.errors = errors
  }
}
